function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

const USD_LIKE = new Set(["USD", "USDT", "USDC", "DAI"]);

function roundMoney(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function currencyOf(value, fallback = "ARS") {
  return String(value || fallback).trim().toUpperCase() || fallback;
}

function moneyLabel(value, currency = "ARS") {
  const code = currencyOf(currency);
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: code,
      maximumFractionDigits: code === "ARS" ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
  }
}

function numberLabel(value, maximumFractionDigits = 2) {
  return Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits });
}

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "long" }).format(date);
}

function shortDateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
}

function localCalendarDay(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

function paymentTiming(dueAt, paidAt) {
  const dueDay = localCalendarDay(dueAt);
  const paidDay = localCalendarDay(paidAt);
  if (dueDay == null || paidDay == null) return null;
  const days = Math.round((paidDay - dueDay) / 86400000);
  if (days < 0) {
    const absolute = Math.abs(days);
    return {
      days,
      tone: "early",
      shortLabel: `${absolute} ${absolute === 1 ? "día" : "días"} antes`,
      label: `Pagó ${absolute} ${absolute === 1 ? "día" : "días"} antes del vencimiento`,
    };
  }
  if (days > 0) {
    return {
      days,
      tone: "late",
      shortLabel: `${days} ${days === 1 ? "día" : "días"} tarde`,
      label: `Pagó con ${days} ${days === 1 ? "día" : "días"} de atraso`,
    };
  }
  return { days: 0, tone: "ontime", shortLabel: "En término", label: "Pagó en término" };
}

function paymentTimingSummary(rows = []) {
  const timings = rows
    .map((row) => paymentTiming(row?.dueAt, row?.paidAt))
    .filter(Boolean);
  const early = timings.filter((row) => row.days < 0);
  const ontime = timings.filter((row) => row.days === 0);
  const late = timings.filter((row) => row.days > 0);
  const signedAverage = timings.length
    ? Math.round(timings.reduce((sum, row) => sum + row.days, 0) / timings.length)
    : null;
  return { total: timings.length, early: early.length, ontime: ontime.length, late: late.length, signedAverage };
}

function monthKey(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}$/.test(String(value))) return String(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function periodLabelFromKey(key) {
  const match = String(key || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return key || "—";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1, 12));
  const label = new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric", timeZone: "UTC" }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function dueDateForPeriod(periodKey, collectionDay = 1) {
  const match = String(periodKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Math.max(1, Math.min(31, Number(collectionDay || 1)));
  const lastDay = new Date(Date.UTC(year, month + 1, 0, 12)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day, lastDay), 12));
}

function addMonths(value, months = 1) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + Number(months || 0), 1, 12));
}

function addInstallmentCadence(value, frequency = "monthly", offset = 0) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const steps = Math.max(0, Number(offset || 0));
  if (frequency === "weekly") {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + (steps * 7));
    return next;
  }
  const day = date.getUTCDate();
  const targetMonth = date.getUTCMonth() + steps;
  const first = new Date(Date.UTC(date.getUTCFullYear(), targetMonth, 1, date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()));
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0, 12)).getUTCDate();
  first.setUTCDate(Math.min(day, lastDay));
  return first;
}

function firstActualInstallmentAnchor(agreement = {}, term = {}) {
  const termId = idOf(term);
  if (!termId) return null;
  const payments = agreement?.payments || [];
  const linkedIds = new Set((agreement?.paymentPlans || [])
    .filter((plan) => (
      idOf(plan.projectTermId) === termId
      && plan.source === "term_schedule"
      && plan.status !== "cancelled"
      && idOf(plan.linkedTransactionId)
    ))
    .map((plan) => idOf(plan.linkedTransactionId)));

  // Para imprimir, la misma regla que usa la conciliación: la primera fecha real
  // de un pago perteneciente a la financiación es el ancla. No confiamos en un
  // nombre viejo como "Cuota 1" ni en el scheduleIndex que haya traído la importación.
  const chronologicalPayment = payments
    .filter((row) => idOf(row.projectTermId) === termId || linkedIds.has(idOf(row)))
    .filter((row) => row?.occurredAt || row?.createdAt)
    .slice()
    .sort((a, b) => new Date(a.occurredAt || a.createdAt || 0) - new Date(b.occurredAt || b.createdAt || 0))[0] || null;
  if (chronologicalPayment) {
    const date = new Date(chronologicalPayment.occurredAt || chronologicalPayment.createdAt);
    if (!Number.isNaN(date.getTime())) return date;
  }

  const historicalFirst = (agreement?.financial?.contractProgress?.financing?.historicalPayments || [])
    .slice()
    .sort((a, b) => new Date(a?.occurredAt || 0) - new Date(b?.occurredAt || 0))[0];
  if (historicalFirst?.occurredAt) {
    const date = new Date(historicalFirst.occurredAt);
    if (!Number.isNaN(date.getTime())) return date;
  }

  const financing = agreement?.financial?.contractProgress?.financing || null;
  if (idOf(financing?.termId) === termId && financing?.firstDueAt) {
    const fallback = new Date(financing.firstDueAt);
    if (!Number.isNaN(fallback.getTime())) return fallback;
  }

  if (term?.installmentFirstDueAt) {
    const configured = new Date(term.installmentFirstDueAt);
    if (!Number.isNaN(configured.getTime())) return configured;
  }
  return null;
}

function installmentDueAtForPrint(agreement = {}, term = {}, plan = {}) {
  if (String(term?.billingMode || "") !== "installments") return plan?.dueAt || null;
  const scheduleIndex = Number(plan?.scheduleIndex || 0);
  const planDueAt = plan?.dueAt ? new Date(plan.dueAt) : null;
  if (scheduleIndex <= 0) return planDueAt && !Number.isNaN(planDueAt.getTime()) ? planDueAt : null;

  // Para una cuota numerada, el vencimiento contractual se deriva SIEMPRE del
  // ancla pactada + cadencia. No confiamos en dueAt persistido porque una versión
  // vieja pudo haberlo corrido usando la fecha real del primer pago.
  const configuredAnchor = term?.installmentFirstDueAt ? new Date(term.installmentFirstDueAt) : null;
  const anchor = configuredAnchor && !Number.isNaN(configuredAnchor.getTime())
    ? configuredAnchor
    : firstActualInstallmentAnchor(agreement, term);
  const rebuilt = anchor
    ? addInstallmentCadence(anchor, term?.installmentFrequency === "weekly" ? "weekly" : "monthly", scheduleIndex - 1)
    : null;
  if (rebuilt && !Number.isNaN(new Date(rebuilt).getTime())) return rebuilt;
  return planDueAt && !Number.isNaN(planDueAt.getTime()) ? planDueAt : null;
}

function addCurrency(target, currency, amount) {
  const code = currencyOf(currency);
  target[code] = roundMoney(Number(target[code] || 0) + Number(amount || 0));
}

function activeContractTerms(agreement = {}) {
  return (agreement?.terms || []).filter((term) => (
    term.status !== "cancelled"
    && term.billingMode !== "monthly"
    && term.billingMode !== "included"
    && Number(term.expectedTotal ?? term.amount ?? 0) > 0
  ));
}

function isRecurringProjectPayment(payment = {}) {
  return Boolean(
    idOf(payment.recurringPlanId)
    || String(payment?.source?.kind || "") === "recurring"
    || idOf(payment?.metadata?.monthlyAgreementHistory?.projectTermId)
  );
}

function importedPaymentOrder(payment = {}) {
  const match = String(payment?.metadata?.sourceRows || "").match(/\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
}

function sortPaymentsChronologically(payments = []) {
  return payments.slice().sort((left, right) => {
    const leftDate = new Date(left?.occurredAt || left?.createdAt || 0).getTime();
    const rightDate = new Date(right?.occurredAt || right?.createdAt || 0).getTime();
    if (leftDate !== rightDate) return leftDate - rightDate;
    const leftOrder = importedPaymentOrder(left);
    const rightOrder = importedPaymentOrder(right);
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return idOf(left).localeCompare(idOf(right));
  });
}

function commonParties(agreement = {}) {
  const client = agreement?.initiative?.client || null;
  return {
    issuer: {
      name: agreement?.issuer?.name || "Owner WebProfit",
      email: agreement?.issuer?.email || "",
    },
    client: client ? {
      name: client.name || "",
      company: client.company || "",
      email: client.email || "",
      phone: client.phone || "",
    } : null,
    project: agreement?.initiative?.name || "Proyecto",
  };
}

function termForPayment(agreement = {}, payment = {}, preferredTermId = null) {
  const terms = agreement?.terms || [];
  const financingTermId = agreement?.financial?.contractProgress?.financing?.termId || null;
  const targetId = idOf(preferredTermId || payment.projectTermId || financingTermId);
  return terms.find((row) => idOf(row) === targetId) || null;
}

function fxDetails(payment = {}, term = {}, fallback = {}) {
  const frozen = payment?.metadata?.fxSettlement || {};
  const rate = Number(frozen.rateARSPerUSD || fallback.fxRateARSPerUSD || 0) || null;
  const quote = frozen.quote || fallback.fxQuote || term?.fxPolicy?.quote || "";
  const side = frozen.side || fallback.fxSide || term?.fxPolicy?.side || "";
  const quotedAt = frozen.quotedAt || fallback.fxQuotedAt || payment.occurredAt || null;
  const provider = frozen.provider || fallback.fxProvider || "";
  const contractCurrency = currencyOf(frozen.contractCurrency || fallback.contractCurrency || term?.currency || "ARS");
  const paymentCurrency = currencyOf(payment.currency || frozen.paymentCurrency || fallback.currency || "ARS");
  const paymentAmount = roundMoney(payment.amount ?? frozen.paymentAmount ?? fallback.amount ?? 0);
  let usdEquivalent = null;
  if (contractCurrency === "USD" && Number(frozen.settledAmount || fallback.contractValue || 0) > 0) {
    usdEquivalent = roundMoney(frozen.settledAmount || fallback.contractValue);
  } else if (USD_LIKE.has(paymentCurrency)) {
    usdEquivalent = paymentAmount;
  } else if (paymentCurrency === "ARS" && rate) {
    usdEquivalent = roundMoney(paymentAmount / rate);
  }
  return {
    rate,
    quote,
    side,
    quotedAt,
    provider,
    contractCurrency,
    usdEquivalent,
    settledAmount: Number(frozen.settledAmount || fallback.contractValue || 0) > 0
      ? roundMoney(frozen.settledAmount || fallback.contractValue)
      : null,
  };
}

function fxNote(details = {}, periodKey = "") {
  if (!details?.rate) return "";
  const quote = [details.quote ? `dólar ${details.quote}` : "dólar", details.side].filter(Boolean).join(" ");
  const period = periodKey ? ` · período ${periodLabelFromKey(periodKey)}` : "";
  const provider = details.provider ? ` · ${details.provider}` : "";
  return `${quote} · ${moneyLabel(details.rate, "ARS")} por USD · cotización ${shortDateLabel(details.quotedAt)}${period}${provider}`;
}

function paymentPeriodKey(payment = {}, fallbackDate = null) {
  return String(
    payment?.metadata?.monthlyAgreementHistory?.periodKey
    || payment?.recurringPeriodKey
    || monthKey(fallbackDate || payment?.occurredAt),
  );
}

function contractPaymentRows(agreement = {}, terms = []) {
  const termIds = new Set(terms.map(idOf));
  const baseTerm = terms.find((term) => term.kind === "base") || terms[0] || null;
  // Owner ya considera los ingresos del proyecto sin bloque como cobertura implícita
  // del contrato base. La factura refleja la misma verdad para no mostrar un total
  // cobrado que después no pueda explicarse con sus movimientos reales.
  const payments = (agreement?.payments || []).filter((row) => {
    // Un movimiento atribuido explícitamente a un término contractual sí pertenece
    // a la factura. En cambio, un cobro recurrente sin término NO se adopta
    // implícitamente como pago del contrato base: las mensualidades tienen su
    // propio comprobante y el resumen contractual del backend usa la misma regla.
    if (termIds.has(idOf(row.projectTermId))) return true;
    return !idOf(row.projectTermId) && Boolean(baseTerm) && !isRecurringProjectPayment(row);
  });
  const paymentsById = new Map(payments.map((row) => [idOf(row), row]));
  const historicalRows = agreement?.financial?.contractProgress?.financing?.historicalPayments || [];
  const historicalById = new Map(historicalRows.map((row) => [idOf(row), row]));
  const seenPaymentIds = new Set();
  const rows = [];
  const activePlans = (agreement?.paymentPlans || [])
    .filter((plan) => plan.status !== "cancelled" && termIds.has(idOf(plan.projectTermId)));

  // Defensa adicional para documentos históricos: aunque la base todavía tenga
  // enlaces viejos (p. ej. un pago de julio pegado a "Cuota 18"), la factura arma
  // el emparejamiento canónico por fecha real del pago y scheduleIndex. El backend
  // persiste esta misma normalización, pero el comprobante no depende de que esa
  // migración haya terminado correctamente para contar la verdad.
  const canonicalPaymentByPlanId = new Map();
  terms.filter((term) => String(term.billingMode || "") === "installments").forEach((term) => {
    const termId = idOf(term);
    const termPlans = activePlans
      .filter((plan) => idOf(plan.projectTermId) === termId)
      .slice()
      .sort((a, b) => Number(a.scheduleIndex || 0) - Number(b.scheduleIndex || 0) || new Date(a.dueAt || 0) - new Date(b.dueAt || 0));
    const linkedIds = new Set(termPlans.map((plan) => idOf(plan.linkedTransactionId)).filter(Boolean));
    const candidates = sortPaymentsChronologically(payments.filter((payment) => (
      idOf(payment.projectTermId) === termId
      || linkedIds.has(idOf(payment))
      || (idOf(term) === idOf(baseTerm) && !idOf(payment.projectTermId) && !isRecurringProjectPayment(payment))
    )));
    termPlans.forEach((plan, index) => {
      if (candidates[index]) canonicalPaymentByPlanId.set(idOf(plan), candidates[index]);
    });
  });

  activePlans
    .sort((a, b) => {
      const ai = Number(a.scheduleIndex || 0);
      const bi = Number(b.scheduleIndex || 0);
      if (ai && bi && idOf(a.projectTermId) === idOf(b.projectTermId) && ai !== bi) return ai - bi;
      return new Date(a.dueAt || 0) - new Date(b.dueAt || 0);
    })
    .forEach((plan) => {
      const term = terms.find((row) => idOf(row) === idOf(plan.projectTermId)) || null;
      const payment = String(term?.billingMode || "") === "installments"
        ? (canonicalPaymentByPlanId.get(idOf(plan)) || null)
        : (paymentsById.get(idOf(plan.linkedTransactionId)) || null);
      const historical = payment ? historicalById.get(idOf(payment)) || {} : {};
      if (payment) seenPaymentIds.add(idOf(payment));
      const fx = payment ? fxDetails(payment, term, historical) : null;
      const expectedCurrency = currencyOf(plan.currency || term?.installmentCurrency || term?.currency || "ARS");
      const actualCurrency = payment ? currencyOf(payment.currency || expectedCurrency) : null;
      const expectedAmount = roundMoney(plan.amount || 0);
      const rawActualAmount = payment ? roundMoney(payment.amount || 0) : null;
      const reallocatedAmount = payment ? roundMoney(historical.reallocatedAmount || 0) : 0;
      const bonusAmount = payment ? roundMoney(historical.bonusAmount || 0) : 0;
      const actualAmount = payment ? roundMoney(Number(rawActualAmount || 0) + reallocatedAmount) : null;
      const coveredAmount = payment ? roundMoney(Number(actualAmount || 0) + bonusAmount) : null;
      const effectiveDueAt = installmentDueAtForPrint(agreement, term, plan);
      const effectivePeriodKey = monthKey(effectiveDueAt);
      const expectedUsdAtPaymentRate = fx?.rate && expectedCurrency === "ARS" ? roundMoney(expectedAmount / fx.rate) : (expectedCurrency === "USD" ? expectedAmount : null);
      const variance = payment && actualCurrency === expectedCurrency ? roundMoney(Number(coveredAmount || 0) - expectedAmount) : null;
      const orphanedPaidPlan = !payment && plan.status === "paid";
      rows.push({
        key: idOf(plan) || `plan-${rows.length + 1}`,
        termTitle: term?.title || "Contrato",
        installmentNumber: Number(plan.scheduleIndex || historical.installmentNumber || 0) || null,
        installmentCount: term?.installmentCountVariable ? null : (Number(term?.installmentCount || 0) || null),
        periodKey: effectivePeriodKey,
        dueAt: effectiveDueAt,
        expectedAmount,
        expectedCurrency,
        paid: Boolean(payment),
        paidAt: payment?.occurredAt || null,
        actualAmount,
        rawActualAmount,
        reallocatedAmount,
        bonusAmount,
        bonusCurrency: expectedCurrency,
        coveredAmount,
        actualCurrency,
        usdEquivalent: historical.contractCurrency === "USD" && Number(historical.contractValue || 0) > 0
          ? roundMoney(historical.contractValue)
          : (fx?.rate && actualCurrency === "ARS" && actualAmount != null ? roundMoney(Number(actualAmount || 0) / fx.rate) : fx?.usdEquivalent),
        expectedUsdAtPaymentRate,
        contractValue: Number(historical.contractValue || 0) > 0 ? roundMoney(historical.contractValue) : fx?.settledAmount,
        contractCurrency: currencyOf(historical.contractCurrency || term?.currency || "ARS"),
        fxRateARSPerUSD: fx?.rate || historical.fxRateARSPerUSD || null,
        fxNote: fxNote(fx, effectivePeriodKey),
        variance,
        description: [payment?.description || historical.description || plan.title || "", reallocatedAmount > 0.005 ? `${moneyLabel(reallocatedAmount, expectedCurrency)} reasignados desde excedente histórico de servidor` : "", bonusAmount > 0.005 ? `${moneyLabel(bonusAmount, expectedCurrency)} bonificados` : ""].filter(Boolean).join(" · "),
        status: payment
          ? (bonusAmount > 0.005 ? "Pagado + bonificación" : (reallocatedAmount > 0.005 ? "Pagado + reasignación" : "Pagado"))
          : orphanedPaidPlan
            ? "Revisión · pago sin movimiento"
            : (plan.derivedStatus === "overdue" ? "Pendiente vencido" : "Pendiente"),
      });
    });

  payments
    .filter((payment) => !seenPaymentIds.has(idOf(payment)))
    .sort((a, b) => new Date(a.occurredAt || 0) - new Date(b.occurredAt || 0))
    .forEach((payment) => {
      const term = terms.find((row) => idOf(row) === idOf(payment.projectTermId)) || (!idOf(payment.projectTermId) ? baseTerm : null);
      const historical = historicalById.get(idOf(payment)) || {};
      const fx = fxDetails(payment, term, historical);
      rows.push({
        key: idOf(payment) || `payment-${rows.length + 1}`,
        termTitle: term?.title || "Contrato",
        installmentNumber: Number(historical.installmentNumber || 0) || null,
        installmentCount: term?.installmentCountVariable ? null : (Number(term?.installmentCount || 0) || null),
        periodKey: monthKey(payment.occurredAt),
        dueAt: null,
        expectedAmount: null,
        expectedCurrency: null,
        paid: true,
        paidAt: payment.occurredAt || null,
        actualAmount: roundMoney(payment.amount || 0),
        actualCurrency: currencyOf(payment.currency || "ARS"),
        usdEquivalent: fx.usdEquivalent,
        expectedUsdAtPaymentRate: null,
        contractValue: fx.settledAmount || (Number(historical.contractValue || 0) > 0 ? roundMoney(historical.contractValue) : null),
        contractCurrency: currencyOf(historical.contractCurrency || term?.currency || "ARS"),
        fxRateARSPerUSD: fx.rate || historical.fxRateARSPerUSD || null,
        fxNote: fxNote(fx, monthKey(payment.occurredAt)),
        variance: null,
        description: payment.description || historical.description || "Pago imputado al contrato",
        status: "Pagado · sin cuota vinculada",
      });
    });

  return rows.sort((a, b) => new Date(a.dueAt || a.paidAt || 0) - new Date(b.dueAt || b.paidAt || 0));
}

export function buildContractPrintDocument(agreement = {}) {
  const terms = activeContractTerms(agreement);
  const totals = {};
  const paid = {};
  const bonus = {};
  const covered = {};
  const outstanding = {};
  const actualReceived = {};
  const rows = contractPaymentRows(agreement, terms);

  const lines = terms.map((term, index) => {
    const currency = currencyOf(term.currency || "ARS");
    const expected = roundMoney(term.expectedTotal ?? term.amount ?? 0);
    const paidAmount = roundMoney(term.paidInTermCurrency || 0);
    const bonusAmount = roundMoney(term.bonusInTermCurrency || 0);
    const coveredAmount = roundMoney(term.coveredInTermCurrency ?? Math.min(expected, paidAmount + bonusAmount));
    const pending = roundMoney(Math.max(0, term.outstanding ?? (expected - coveredAmount)));
    addCurrency(totals, currency, expected);
    addCurrency(paid, currency, paidAmount);
    addCurrency(bonus, currency, bonusAmount);
    addCurrency(covered, currency, coveredAmount);
    addCurrency(outstanding, currency, pending);
    return {
      label: `${term.sequence ? `${term.sequence}. ` : `${index + 1}. `}${term.title || "Contrato"}`,
      detail: term.billingMode === "installments"
        ? `${term.installmentCountVariable ? "Cantidad variable" : `${term.installmentCount || "—"} cuotas`} · ${term.installmentFrequency === "weekly" ? "semanales" : "mensuales"}${term.installmentAmount ? ` · cuota nominal ${moneyLabel(term.installmentAmount, term.installmentCurrency || term.currency)}` : ""}${term.installmentCountVariable ? ` · hasta completar ${moneyLabel(term.amount || 0, term.currency || "ARS")}` : ""}`
        : "Pago único",
      amount: expected,
      currency,
      effectiveFrom: term.effectiveFrom || null,
      fxPolicy: term?.fxPolicy?.configured && currency === "USD"
        ? `Dólar ${term.fxPolicy.quote || "blue"} · ${term.fxPolicy.side || "venta"}`
        : "",
    };
  });

  rows.filter((row) => row.paid && row.actualAmount != null).forEach((row) => addCurrency(actualReceived, row.actualCurrency, row.actualAmount));

  return {
    kind: "contract",
    title: "Factura global del contrato",
    disclaimer: "Documento comercial interno · no es comprobante fiscal",
    reference: `CONTRATO-${idOf(agreement?.initiative).slice(-8).toUpperCase() || "OWNER"}`,
    issuedAt: new Date(),
    ...commonParties(agreement),
    lines,
    paymentRows: rows,
    timingSummary: paymentTimingSummary(rows),
    totals,
    paid,
    bonus,
    covered,
    actualReceived,
    outstanding,
    notes: "Este documento separa dinero real, reasignaciones y bonificaciones. Una bonificación completa comercialmente una cuota sin inventar caja. Los excedentes históricos del servidor sólo pueden recomponer cuotas parciales cuando provienen de una división de ese mismo contrato. Cuando el contrato está expresado en USD y el pago fue en ARS, la equivalencia usa la cotización histórica congelada para la fecha de ese pago. Las mensualidades recurrentes tienen su propio documento.",
  };
}

export function buildInstallmentReceiptDocument(agreement = {}, payment = {}) {
  const terms = agreement?.terms || [];
  const financingTermId = agreement?.financial?.contractProgress?.financing?.termId || null;
  const term = terms.find((row) => idOf(row) === idOf(payment.projectTermId || financingTermId)) || null;
  const plans = agreement?.paymentPlans || [];
  const plan = plans.find((row) => idOf(row.linkedTransactionId) === idOf(payment))
    || plans.find((row) => Number(row.scheduleIndex || 0) === Number(payment.installmentNumber || 0) && idOf(row.projectTermId) === idOf(term));
  const historical = (agreement?.financial?.contractProgress?.financing?.historicalPayments || []).find((row) => idOf(row) === idOf(payment)) || payment;
  const installmentCountVariable = Boolean(term?.installmentCountVariable || agreement?.financial?.contractProgress?.financing?.installmentCountVariable);
  const installmentCount = installmentCountVariable ? 0 : Number(term?.installmentCount || agreement?.financial?.contractProgress?.financing?.installmentCount || 0);
  const installmentNumber = Number(payment.installmentNumber || plan?.scheduleIndex || historical?.installmentNumber || 0) || null;
  const referenceId = idOf(payment) || idOf(plan);
  const fx = fxDetails(payment, term, historical);
  const expectedAmount = Number(plan?.amount || historical?.nominalInstallmentAmount || term?.installmentAmount || 0) > 0
    ? roundMoney(plan?.amount || historical?.nominalInstallmentAmount || term?.installmentAmount)
    : null;
  const expectedCurrency = currencyOf(plan?.currency || historical?.installmentCurrency || term?.installmentCurrency || term?.currency || "ARS");
  const actualAmount = roundMoney(payment.amount ?? historical?.amount ?? plan?.amount ?? 0);
  const actualCurrency = currencyOf(payment.currency || historical?.currency || plan?.currency || term?.installmentCurrency || term?.currency || "ARS");
  const contractValue = fx.settledAmount || (Number(historical?.contractValue || payment.contractValue || 0) > 0 ? roundMoney(historical?.contractValue || payment.contractValue) : null);
  const contractCurrency = currencyOf(historical?.contractCurrency || payment.contractCurrency || term?.currency || "ARS");
  const effectiveDueAt = installmentDueAtForPrint(agreement, term, plan || {});
  const periodKey = monthKey(effectiveDueAt || payment.occurredAt || historical?.occurredAt);
  const expectedUsdAtPaymentRate = fx.rate && expectedCurrency === "ARS" ? roundMoney(expectedAmount / fx.rate) : (expectedCurrency === "USD" ? expectedAmount : null);
  const variance = expectedAmount != null && actualCurrency === expectedCurrency ? roundMoney(actualAmount - expectedAmount) : null;

  return {
    kind: "installment_receipt",
    title: "Comprobante de pago de cuota",
    disclaimer: "Constancia comercial de pago · no es comprobante fiscal",
    reference: `CUOTA-${referenceId.slice(-8).toUpperCase() || "OWNER"}`,
    issuedAt: new Date(),
    paidAt: payment.occurredAt || historical?.occurredAt || plan?.paidAt || null,
    ...commonParties(agreement),
    termTitle: term?.title || "Contrato",
    periodLabel: installmentNumber
      ? `Cuota ${installmentNumber}${installmentCount ? ` de ${installmentCount}` : ""}${periodKey ? ` · ${periodLabelFromKey(periodKey)}` : ""}`
      : (periodKey ? `Cuota del contrato · ${periodLabelFromKey(periodKey)}` : "Cuota del contrato"),
    amount: actualAmount,
    currency: actualCurrency,
    expectedAmount,
    expectedCurrency,
    expectedDueAt: effectiveDueAt,
    timing: paymentTiming(effectiveDueAt, payment.occurredAt || historical?.occurredAt || plan?.paidAt || null),
    contractValue,
    contractCurrency,
    usdEquivalent: fx.usdEquivalent || (contractCurrency === "USD" ? contractValue : null),
    expectedUsdAtPaymentRate,
    fxRateARSPerUSD: fx.rate || historical?.fxRateARSPerUSD || null,
    fxQuote: fx.quote || historical?.fxQuote || "",
    fxSide: fx.side || historical?.fxSide || "",
    fxQuotedAt: fx.quotedAt || historical?.fxQuotedAt || null,
    fxProvider: fx.provider || "",
    fxNote: fxNote(fx, periodKey),
    variance,
    description: payment.description || historical?.description || plan?.title || "Pago realizado",
  };
}

export function monthlyPaymentsForTerm(agreement = {}, termId) {
  const targetId = idOf(termId);
  if (!targetId) return [];
  return (agreement?.payments || [])
    .filter((payment) => idOf(payment.projectTermId) === targetId)
    .filter((payment) => (
      String(payment?.source?.kind || "") === "recurring"
      || idOf(payment?.metadata?.monthlyAgreementHistory?.projectTermId) === targetId
      || Boolean(payment.recurringPlanId)
    ))
    .slice()
    .sort((a, b) => new Date(b.occurredAt || 0) - new Date(a.occurredAt || 0));
}

function monthlyExpectationsForTerm(agreement = {}, termId) {
  const targetId = idOf(termId);
  return (agreement?.monthlyPeriodExpectations || [])
    .filter((row) => idOf(row.projectTermId) === targetId)
    .slice()
    .sort((a, b) => String(a.periodKey || "").localeCompare(String(b.periodKey || "")));
}

function expectedMonthlyPeriodKeys(agreement = {}, term = {}, payments = []) {
  const explicitPaymentKeys = payments.map((payment) => paymentPeriodKey(payment)).filter(Boolean);
  const snapshotKeys = monthlyExpectationsForTerm(agreement, idOf(term)).map((row) => String(row.periodKey || "")).filter(Boolean);
  const todayKey = monthKey(new Date());
  const anchor = term.effectiveFrom || term.createdAt || payments[0]?.occurredAt || new Date();
  let cursor = dueDateForPeriod(monthKey(anchor), 1);
  if (!cursor) return [...new Set([...explicitPaymentKeys, ...snapshotKeys])].sort();
  const cadence = Math.max(1, Number(term.cadenceMonths || 1));
  const maxCycles = Math.max(1, Math.min(120, Number(term.expectedCycles || 120)));
  const generated = [];
  for (let index = 0; index < maxCycles; index += 1) {
    const key = monthKey(cursor);
    if (!key) break;
    if (key > todayKey && !explicitPaymentKeys.includes(key) && !snapshotKeys.includes(key)) break;
    generated.push(key);
    cursor = addMonths(cursor, cadence);
    if (!cursor) break;
  }
  return [...new Set([...generated, ...explicitPaymentKeys, ...snapshotKeys])].sort();
}

function monthlyExpectationForPeriod(agreement = {}, term = {}, periodKey = "", periodPayments = []) {
  const normalization = agreement?.financial?.historicalNormalization;
  const normalizedMonthly = idOf(normalization?.monthlyTermId) === idOf(term) ? normalization?.monthlyNormalization : null;
  const normalized = (normalizedMonthly?.periods || []).find((row) => String(row.periodKey || "") === String(periodKey || ""));
  if (normalized?.expectedAmount != null && normalized?.expectedCurrency) {
    return {
      amount: roundMoney(normalized.expectedAmount || 0),
      currency: currencyOf(normalized.expectedCurrency || "ARS"),
      dueAt: dueDateForPeriod(periodKey, term.collectionDay || 1),
      source: "historical_policy",
    };
  }
  const materialized = monthlyExpectationsForTerm(agreement, idOf(term)).find((row) => String(row.periodKey || "") === String(periodKey || ""));
  if (materialized && Number(materialized.expectedAmount || 0) >= 0) {
    return {
      amount: roundMoney(materialized.expectedAmount || 0),
      currency: currencyOf(materialized.currency || term.currency || "ARS"),
      dueAt: materialized.dueAt || dueDateForPeriod(periodKey, term.collectionDay || 1),
      source: materialized.expectationSource || "materialized",
    };
  }
  const historicalSnapshot = periodPayments
    .map((payment) => payment?.metadata?.monthlyAgreementHistory || null)
    .find((row) => row && Number(row.expectedAmount || 0) >= 0 && row.expectedCurrency);
  if (historicalSnapshot) {
    return {
      amount: roundMoney(historicalSnapshot.expectedAmount || 0),
      currency: currencyOf(historicalSnapshot.expectedCurrency || term.currency || "ARS"),
      dueAt: historicalSnapshot.expectedDueAt || dueDateForPeriod(periodKey, historicalSnapshot.collectionDay || term.collectionDay || 1),
      source: "historical_snapshot",
    };
  }
  return {
    amount: roundMoney(term.amount || 0),
    currency: currencyOf(term.currency || "ARS"),
    dueAt: dueDateForPeriod(periodKey, term.collectionDay || 1),
    source: "current_term_fallback",
  };
}

export function buildMonthlyStatementDocument(agreement = {}, term = {}) {
  const payments = monthlyPaymentsForTerm(agreement, idOf(term)).slice().sort((a, b) => new Date(a.occurredAt || 0) - new Date(b.occurredAt || 0));
  const paymentsByPeriod = new Map();
  payments.forEach((payment) => {
    const key = paymentPeriodKey(payment);
    if (!key) return;
    if (!paymentsByPeriod.has(key)) paymentsByPeriod.set(key, []);
    paymentsByPeriod.get(key).push(payment);
  });
  const currentCurrency = currencyOf(term.currency || "ARS");
  const currentAmount = roundMoney(term.amount || 0);
  const legacy = term?.legacyMonthlyConcession || {};
  const normalization = agreement?.financial?.historicalNormalization;
  const normalizedMonthly = idOf(normalization?.monthlyTermId) === idOf(term) ? normalization?.monthlyNormalization : null;
  const normalizedByPeriod = new Map((normalizedMonthly?.periods || []).map((row) => [String(row.periodKey || ""), row]));
  const actualReceived = {};
  const rawAssociatedReceived = {};
  const expectedTotals = {};
  const appliedTotals = {};
  const bonusTotals = {};
  const coveredTotals = {};
  const excessTotals = {};
  const rows = [];

  const statementPeriodKeys = [...new Set([
    ...expectedMonthlyPeriodKeys(agreement, term, payments),
    ...(normalizedMonthly?.periods || []).map((row) => String(row.periodKey || "")).filter(Boolean),
  ])].sort();

  statementPeriodKeys.forEach((periodKey) => {
    const periodPayments = paymentsByPeriod.get(periodKey) || [];
    const normalized = normalizedByPeriod.get(String(periodKey || "")) || null;
    const expectation = monthlyExpectationForPeriod(agreement, term, periodKey, periodPayments);
    addCurrency(expectedTotals, expectation.currency, expectation.amount);
    const dueAt = expectation.dueAt || dueDateForPeriod(periodKey, term.collectionDay || 1);

    if (!periodPayments.length) {
      const bonusAmount = normalized?.bonusAmount != null ? roundMoney(normalized.bonusAmount) : 0;
      if (bonusAmount > 0.005 && currencyOf(normalized?.expectedCurrency || expectation.currency) === expectation.currency) {
        addCurrency(bonusTotals, expectation.currency, bonusAmount);
        addCurrency(coveredTotals, expectation.currency, bonusAmount);
        rows.push({
          key: periodKey, periodKey, dueAt, expectedAmount: expectation.amount, expectedCurrency: expectation.currency,
          expectationSource: expectation.source, paid: false, covered: true,
          bonusAmount, bonusCurrency: expectation.currency, status: "Bonificado",
          description: `Bonificación histórica para completar ${moneyLabel(expectation.amount, expectation.currency)} del período.`,
        });
      } else {
        rows.push({
          key: periodKey, periodKey, dueAt, expectedAmount: expectation.amount, expectedCurrency: expectation.currency,
          expectationSource: expectation.source, paid: false, covered: false,
          status: dueAt && new Date(dueAt).getTime() > Date.now() ? "A vencer" : "Sin pago registrado",
        });
      }
      return;
    }

    const allocationsByPayment = new Map((normalized?.paymentAllocations || []).map((row) => [idOf(row.id), row]));
    const prepared = periodPayments.map((payment) => {
      const fx = fxDetails(payment, term);
      const rawAmount = roundMoney(payment.amount || 0);
      const actualCurrency = currencyOf(payment.currency || expectation.currency);
      const allocation = allocationsByPayment.get(idOf(payment)) || null;
      const actualAmount = allocation && actualCurrency === currencyOf(normalized?.expectedCurrency || expectation.currency)
        ? roundMoney(allocation.appliedAmount || 0)
        : rawAmount;
      const excessAmount = allocation ? roundMoney(allocation.excessAmount || 0) : 0;
      addCurrency(rawAssociatedReceived, actualCurrency, rawAmount);
      addCurrency(actualReceived, actualCurrency, actualAmount);
      let appliedAmount = null;
      if (expectation.currency === "USD" && fx.rate && actualCurrency === "ARS") appliedAmount = roundMoney(actualAmount / fx.rate);
      else if (expectation.currency === "USD" && fx.usdEquivalent != null && rawAmount > 0) appliedAmount = roundMoney(Number(fx.usdEquivalent || 0) * (actualAmount / rawAmount));
      else if (actualCurrency === expectation.currency) appliedAmount = actualAmount;
      const usdEquivalent = fx.rate && actualCurrency === "ARS" ? roundMoney(actualAmount / fx.rate) : (USD_LIKE.has(actualCurrency) ? actualAmount : fx.usdEquivalent);
      return { payment, fx, rawAmount, actualAmount, actualCurrency, appliedAmount, excessAmount, usdEquivalent };
    });

    const periodComparable = prepared.every((row) => row.appliedAmount != null);
    const periodApplied = normalized?.appliedAmount != null && currencyOf(normalized?.expectedCurrency || expectation.currency) === expectation.currency
      ? roundMoney(normalized.appliedAmount)
      : (periodComparable ? roundMoney(prepared.reduce((sum, row) => sum + Number(row.appliedAmount || 0), 0)) : null);
    const periodBonus = normalized?.bonusAmount != null && currencyOf(normalized?.expectedCurrency || expectation.currency) === expectation.currency
      ? roundMoney(normalized.bonusAmount)
      : 0;
    const periodExcess = normalized?.excessAmount != null ? roundMoney(normalized.excessAmount) : roundMoney(prepared.reduce((sum, row) => sum + Number(row.excessAmount || 0), 0));
    const periodCovered = periodApplied != null ? roundMoney(periodApplied + periodBonus) : null;
    if (periodApplied != null) addCurrency(appliedTotals, expectation.currency, periodApplied);
    if (periodBonus > 0) addCurrency(bonusTotals, expectation.currency, periodBonus);
    if (periodCovered != null) addCurrency(coveredTotals, expectation.currency, periodCovered);
    if (periodExcess > 0) addCurrency(excessTotals, currencyOf(normalized?.expectedCurrency || expectation.currency), periodExcess);
    const periodVariance = periodCovered != null ? roundMoney(periodCovered - expectation.amount) : null;
    const periodStatus = periodCovered == null
      ? "Pagado"
      : periodCovered + 0.005 < expectation.amount
        ? "Pago parcial"
        : periodBonus > 0 ? "Pagado + bonificación" : "Pagado";

    prepared.forEach(({ payment, fx, rawAmount, actualAmount, actualCurrency, excessAmount, usdEquivalent }, paymentIndex) => {
      const expectedUsdAtPaymentRate = fx.rate && expectation.currency === "ARS"
        ? roundMoney(expectation.amount / fx.rate)
        : (expectation.currency === "USD" ? expectation.amount : null);
      const details = [];
      if (rawAmount > actualAmount + 0.005) details.push(`${moneyLabel(rawAmount, actualCurrency)} asociados · ${moneyLabel(actualAmount, actualCurrency)} aplicados al servidor`);
      if (excessAmount > 0.005) details.push(`${moneyLabel(excessAmount, actualCurrency)} excedentes liberados`);
      rows.push({
        key: `${periodKey}-${idOf(payment) || paymentIndex}`, periodKey, dueAt,
        expectedAmount: paymentIndex === 0 ? expectation.amount : null, expectedCurrency: expectation.currency,
        expectationSource: expectation.source, paid: actualAmount > 0.005, covered: periodCovered != null && periodCovered + 0.005 >= expectation.amount,
        status: paymentIndex === 0 ? periodStatus : `Movimiento ${paymentIndex + 1} del período`, paidAt: payment.occurredAt || null,
        actualAmount, rawActualAmount: rawAmount, actualCurrency,
        bonusAmount: paymentIndex === 0 ? periodBonus : 0, bonusCurrency: expectation.currency,
        excessAmount, excessCurrency: actualCurrency, usdEquivalent, expectedUsdAtPaymentRate,
        contractValue: fx.settledAmount, contractCurrency: currencyOf(fx.contractCurrency || term.currency || "ARS"),
        fxRateARSPerUSD: fx.rate, fxNote: fxNote(fx, periodKey),
        variance: paymentIndex === 0 ? periodVariance : null, varianceCurrency: expectation.currency,
        description: [payment.description || payment.category || term.title || "Mensualidad abonada", ...details].filter(Boolean).join(" · "),
      });
    });
  });

  const distinctPeriods = new Set(rows.map((row) => row.periodKey));
  const coveredPeriods = new Set(rows.filter((row) => row.covered || row.paid).map((row) => row.periodKey));

  return {
    kind: "monthly_statement",
    title: "Factura global de mensualidades",
    disclaimer: "Documento comercial interno · no es comprobante fiscal",
    reference: `MENSUALIDADES-${idOf(term).slice(-8).toUpperCase() || idOf(agreement?.initiative).slice(-8).toUpperCase() || "OWNER"}`,
    issuedAt: new Date(),
    ...commonParties(agreement),
    termTitle: term.title || "Mensualidad", termAmount: currentAmount, termCurrency: currentCurrency,
    historicalMonthlyAmount: legacy.enabled ? roundMoney(legacy.historicalChargeAmount || 0) : 0,
    historicalMonthlyCurrency: currencyOf(legacy.historicalChargeCurrency || "ARS"),
    historicalBonusThrough: legacy.bonifyThroughAt || null,
    collectionDay: term.collectionDay || null, effectiveFrom: term.effectiveFrom || null,
    recurringForever: term.recurringForever === true || !Number(term.expectedCycles || 0), expectedCycles: term.expectedCycles || null,
    fxPolicy: term?.fxPolicy?.configured && currentCurrency === "USD" ? `Dólar ${term.fxPolicy.quote || "blue"} · ${term.fxPolicy.side || "venta"}` : "",
    rows, timingSummary: paymentTimingSummary(rows), expectedPeriodCount: distinctPeriods.size,
    paidPeriodCount: coveredPeriods.size, unpaidPeriodCount: Math.max(0, distinctPeriods.size - coveredPeriods.size),
    expectedTotals, appliedTotals, bonusTotals, coveredTotals, excessTotals, actualReceived, rawAssociatedReceived,
    notes: legacy.enabled && Number(legacy.historicalChargeAmount || 0) > 0
      ? `Hasta ${dateLabel(legacy.bonifyThroughAt || new Date())}, cada mensualidad histórica usa ${moneyLabel(legacy.historicalChargeAmount, legacy.historicalChargeCurrency || "ARS")} como valor estable. Los faltantes se muestran como bonificación y los excedentes no aumentan el precio del servidor; sólo los que nacieron de la división del contrato pueden recomponer cuotas parciales.`
      : "La mensualidad se documenta separada del contrato. Los pagos reales nunca se reemplazan por valores actuales.",
  };
}

export function buildMonthlyReceiptDocument(agreement = {}, term = {}, payment = {}) {
  const periodKey = paymentPeriodKey(payment);
  const referenceId = idOf(payment);
  const history = payment?.metadata?.monthlyAgreementHistory || {};
  const fxBase = fxDetails(payment, term);
  const fx = history.rateARSPerUSD ? {
    ...fxBase,
    rate: Number(history.rateARSPerUSD || 0),
    quote: history.fxQuote || fxBase.quote,
    side: history.fxSide || fxBase.side,
    quotedAt: history.fxQuotedAt || fxBase.quotedAt,
    provider: history.fxProvider || fxBase.provider,
    usdEquivalent: history.settledCurrency === "USD" && history.settledAmount != null ? Number(history.settledAmount) : fxBase.usdEquivalent,
    settledAmount: history.settledAmount ?? fxBase.settledAmount,
    contractCurrency: history.settledCurrency || fxBase.contractCurrency,
  } : fxBase;
  const expectation = monthlyExpectationForPeriod(agreement, term, periodKey, [payment]);
  const expectedAmount = expectation.amount;
  const expectedCurrency = expectation.currency;
  const actualAmount = roundMoney(payment.amount || 0);
  const actualCurrency = currencyOf(payment.currency || expectedCurrency);
  const expectedUsdAtPaymentRate = fx.rate && expectedCurrency === "ARS" ? roundMoney(expectedAmount / fx.rate) : (expectedCurrency === "USD" ? expectedAmount : null);
  const appliedAmount = currencyOf(history.settledCurrency || "") === expectedCurrency && history.settledAmount != null
    ? Number(history.settledAmount || 0)
    : (expectedCurrency === "USD" && fx.usdEquivalent != null ? Number(fx.usdEquivalent || 0) : (actualCurrency === expectedCurrency ? actualAmount : null));
  const bonifiedAmount = currencyOf(history.bonifiedCurrency || "") === expectedCurrency ? roundMoney(history.bonifiedAmount || 0) : 0;
  const variance = appliedAmount != null ? roundMoney(appliedAmount + bonifiedAmount - expectedAmount) : null;
  return {
    kind: "monthly_receipt",
    title: "Comprobante de pago de mensualidad",
    disclaimer: "Constancia comercial de pago · no es comprobante fiscal",
    reference: `MENSUAL-${referenceId.slice(-8).toUpperCase() || "OWNER"}`,
    issuedAt: new Date(),
    paidAt: payment.occurredAt || null,
    ...commonParties(agreement),
    termTitle: term?.title || "Mensualidad",
    periodLabel: periodKey ? `Período ${periodLabelFromKey(periodKey)}` : "Mensualidad",
    amount: actualAmount,
    currency: actualCurrency,
    expectedAmount,
    expectedCurrency,
    expectedDueAt: expectation.dueAt || (periodKey ? dueDateForPeriod(periodKey, term.collectionDay || 1) : null),
    timing: paymentTiming(
      expectation.dueAt || (periodKey ? dueDateForPeriod(periodKey, term.collectionDay || 1) : null),
      payment.occurredAt || null,
    ),
    contractValue: fx.settledAmount,
    contractCurrency: currencyOf(fx.contractCurrency || term.currency || "ARS"),
    usdEquivalent: fx.usdEquivalent,
    expectedUsdAtPaymentRate,
    fxRateARSPerUSD: fx.rate,
    fxQuote: fx.quote,
    fxSide: fx.side,
    fxQuotedAt: fx.quotedAt,
    fxProvider: fx.provider,
    fxNote: fxNote(fx, periodKey),
    bonifiedAmount: bonifiedAmount > 0 ? bonifiedAmount : null,
    bonifiedCurrency: expectedCurrency,
    bonificationReason: history.bonificationReason || "",
    variance,
    varianceCurrency: expectedCurrency,
    description: payment.description || payment.category || term?.title || "Mensualidad abonada",
  };
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function currencyRows(values = {}, label) {
  return Object.entries(values || {}).map(([currency, amount]) => `
    <div class="summary-row"><span>${escapeHtml(label)}</span><strong>${escapeHtml(moneyLabel(amount, currency))}</strong></div>
  `).join("");
}

function partyHtml(label, party) {
  if (!party) return `
    <section class="party"><div class="eyebrow">${escapeHtml(label)}</div><strong>Sin datos cargados</strong></section>
  `;
  return `
    <section class="party">
      <div class="eyebrow">${escapeHtml(label)}</div>
      <strong>${escapeHtml(party.company || party.name || "—")}</strong>
      ${party.company && party.name ? `<div>${escapeHtml(party.name)}</div>` : ""}
      ${party.email ? `<div>${escapeHtml(party.email)}</div>` : ""}
      ${party.phone ? `<div>${escapeHtml(party.phone)}</div>` : ""}
    </section>
  `;
}

function termRowsHtml(lines = []) {
  return lines.map((line) => `
    <tr>
      <td><strong>${escapeHtml(line.label)}</strong>${line.detail ? `<div class="muted">${escapeHtml(line.detail)}</div>` : ""}${line.fxPolicy ? `<div class="muted">Cotización pactada: ${escapeHtml(line.fxPolicy)}</div>` : ""}</td>
      <td>${escapeHtml(dateLabel(line.effectiveFrom))}</td>
      <td class="amount">${escapeHtml(moneyLabel(line.amount, line.currency))}</td>
    </tr>
  `).join("");
}

function fxCell(row = {}) {
  if (!row.fxRateARSPerUSD) return '<span class="muted">—</span>';
  return `<strong>${escapeHtml(moneyLabel(row.fxRateARSPerUSD, "ARS"))}/USD</strong>${row.fxNote ? `<div class="muted">${escapeHtml(row.fxNote)}</div>` : ""}`;
}

function expectedCell(row = {}) {
  if (row.expectedAmount == null || !row.expectedCurrency) return '<span class="muted">Sin cuota vinculada</span>';
  const usd = row.expectedUsdAtPaymentRate != null && row.expectedCurrency !== "USD"
    ? `<div class="muted">≈ ${escapeHtml(moneyLabel(row.expectedUsdAtPaymentRate, "USD"))} a la cotización del pago</div>`
    : "";
  return `<strong>${escapeHtml(moneyLabel(row.expectedAmount, row.expectedCurrency))}</strong>${row.dueAt ? `<div class="muted">Vencía ${escapeHtml(shortDateLabel(row.dueAt))}</div>` : ""}${usd}`;
}

function actualCell(row = {}) {
  const bonusAmount = roundMoney(row.bonusAmount ?? row.bonifiedAmount ?? 0);
  const bonusCurrency = currencyOf(row.bonusCurrency || row.bonifiedCurrency || row.expectedCurrency || row.actualCurrency || "ARS");
  const reallocatedAmount = roundMoney(row.reallocatedAmount || 0);
  const rawAmount = row.rawActualAmount != null ? roundMoney(row.rawActualAmount) : null;
  const appliedAmount = row.actualAmount != null ? roundMoney(row.actualAmount) : null;
  const actualCurrency = row.actualCurrency ? currencyOf(row.actualCurrency) : null;
  const excessAmount = roundMoney(row.excessAmount || 0);
  const excessCurrency = currencyOf(row.excessCurrency || actualCurrency || "ARS");
  const parts = [];

  if (row.paid && appliedAmount != null && actualCurrency) {
    if (rawAmount != null && Math.abs(rawAmount - appliedAmount) > 0.005) {
      parts.push(`<strong>${escapeHtml(moneyLabel(appliedAmount, actualCurrency))}</strong><div class="muted">Aplicado de ${escapeHtml(moneyLabel(rawAmount, actualCurrency))} recibidos</div>`);
    } else if (reallocatedAmount > 0.005 && rawAmount != null) {
      parts.push(`<strong>${escapeHtml(moneyLabel(rawAmount, actualCurrency))}</strong><div class="muted">Pago real</div>`);
    } else {
      parts.push(`<strong>${escapeHtml(moneyLabel(appliedAmount, actualCurrency))}</strong>`);
    }
    if (row.paidAt) parts.push(`<div class="muted">${escapeHtml(shortDateLabel(row.paidAt))}</div>`);
  } else {
    parts.push('<span class="status pending">Sin pago real</span>');
  }

  if (reallocatedAmount > 0.005) parts.push(`<div class="positive">+ ${escapeHtml(moneyLabel(reallocatedAmount, row.expectedCurrency || actualCurrency || "ARS"))} reasignado</div>`);
  if (bonusAmount > 0.005) parts.push(`<div class="positive">+ ${escapeHtml(moneyLabel(bonusAmount, bonusCurrency))} bonificado</div>`);
  if (excessAmount > 0.005) parts.push(`<div class="muted">${escapeHtml(moneyLabel(excessAmount, excessCurrency))} excedente liberado</div>`);

  const coveredAmount = row.coveredAmount != null
    ? roundMoney(row.coveredAmount)
    : (row.expectedCurrency && ((appliedAmount != null && currencyOf(row.actualCurrency || row.expectedCurrency) === currencyOf(row.expectedCurrency)) || bonusAmount > 0)
      ? roundMoney(Number(appliedAmount || 0) + bonusAmount)
      : null);
  if (coveredAmount != null && row.expectedCurrency && bonusAmount > 0.005) {
    parts.push(`<div class="muted">Cubierto: ${escapeHtml(moneyLabel(coveredAmount, row.expectedCurrency))}</div>`);
  }
  return parts.join("");
}

function usdCell(row = {}) {
  if (row.usdEquivalent == null) return '<span class="muted">—</span>';
  return `<strong>${escapeHtml(moneyLabel(row.usdEquivalent, "USD"))}</strong>${row.contractCurrency === "USD" && row.contractValue != null ? '<div class="muted">Imputado al saldo USD</div>' : ""}`;
}

function varianceCell(row = {}) {
  const currency = row.varianceCurrency || row.expectedCurrency || row.actualCurrency || "ARS";
  const bonusAmount = roundMoney(row.bonusAmount ?? row.bonifiedAmount ?? 0);
  if (row.variance == null) {
    return bonusAmount > 0.005 ? '<span class="positive">Cubierto con bonificación</span>' : '<span class="muted">—</span>';
  }
  if (Math.abs(Number(row.variance || 0)) <= 0.005) {
    return bonusAmount > 0.005 ? '<span class="positive">$ 0 · cubierto</span>' : '<span>$ 0</span>';
  }
  const sign = Number(row.variance) > 0 ? "+" : "";
  return `<span class="${Number(row.variance) < 0 ? "negative" : "positive"}">${escapeHtml(`${sign}${moneyLabel(row.variance, currency)}`)}</span>`;
}

function timingCell(row = {}) {
  if (!row.paid || !row.dueAt || !row.paidAt) return '<span class="muted">—</span>';
  const timing = paymentTiming(row.dueAt, row.paidAt);
  if (!timing) return '<span class="muted">—</span>';
  return `<span class="timing-badge ${escapeHtml(timing.tone)}">${escapeHtml(timing.shortLabel)}</span><div class="timing-copy">${escapeHtml(timing.label)}</div>`;
}

function timingSummaryHtml(summary = {}) {
  if (!Number(summary?.total || 0)) return "";
  const average = Number(summary.signedAverage || 0);
  const averageText = average < 0
    ? `${Math.abs(average)} ${Math.abs(average) === 1 ? "día" : "días"} antes`
    : average > 0
      ? `${average} ${average === 1 ? "día" : "días"} tarde`
      : "en término";
  return `
    <div class="timing-overview">
      <div><span class="eyebrow">PUNTUALIDAD DE PAGOS</span><strong>${escapeHtml(summary.total)} pagos comparables</strong><span class="timing-overview-copy">Promedio: ${escapeHtml(averageText)}</span></div>
      <div class="timing-stat early"><strong>${escapeHtml(summary.early || 0)}</strong><span>adelantados</span></div>
      <div class="timing-stat ontime"><strong>${escapeHtml(summary.ontime || 0)}</strong><span>en término</span></div>
      <div class="timing-stat late"><strong>${escapeHtml(summary.late || 0)}</strong><span>atrasados</span></div>
    </div>
  `;
}

function paymentComparisonTable(rows = [], { monthly = false } = {}) {
  if (!rows.length) return '<p class="empty">Todavía no hay cronograma o pagos para mostrar.</p>';
  const body = rows.map((row) => {
    const installment = row.installmentNumber ? `Cuota ${row.installmentNumber}${row.installmentCount ? `/${row.installmentCount}` : ""}` : "";
    const period = row.periodKey ? periodLabelFromKey(row.periodKey) : "—";
    return `
      <tr>
        <td><strong>${escapeHtml(period)}</strong>${installment ? `<div class="muted">${escapeHtml(installment)}</div>` : ""}${!monthly && row.termTitle ? `<div class="muted">${escapeHtml(row.termTitle)}</div>` : ""}<div class="status-line">${escapeHtml(row.status || "")}</div></td>
        <td>${expectedCell(row)}</td>
        <td>${actualCell(row)}</td>
        <td>${timingCell(row)}</td>
        <td>${fxCell(row)}</td>
        <td class="amount">${usdCell(row)}</td>
        <td class="amount">${varianceCell(row)}</td>
      </tr>
      ${row.description ? `<tr class="detail-row"><td colspan="7"><span class="muted"><strong>Nota:</strong> ${escapeHtml(row.description)}</span></td></tr>` : ""}
    `;
  }).join("");
  return `
    <div class="table-wrap">
      <table class="comparison"><thead><tr><th>Período</th><th>Pactado</th><th>Pago real / cobertura</th><th>Desfase</th><th>Dólar aplicado</th><th class="amount">Equiv. USD</th><th class="amount">Diferencia</th></tr></thead><tbody>${body}</tbody></table>
    </div>
  `;
}

function contractBody(document) {
  return `
    <section class="section">
      <h2>Condiciones pactadas del contrato</h2>
      <table><thead><tr><th>Concepto</th><th>Desde</th><th class="amount">Importe contractual</th></tr></thead><tbody>${termRowsHtml(document.lines || [])}</tbody></table>
    </section>
    <section class="section">
      <h2>Cuotas: pactado vs. pagos reales</h2>
      <p class="section-copy">Cada período muestra cuánto correspondía pagar, cuánto ingresó realmente y, cuando existe una liquidación USD, con qué dólar quedó congelada la equivalencia. El desfase compara la fecha real del pago contra su vencimiento pactado.</p>
      ${timingSummaryHtml(document.timingSummary)}
      ${paymentComparisonTable(document.paymentRows || [])}
    </section>
    <div class="summary-grid">
      <div class="summary-card"><div class="eyebrow">CONTRATO</div>${currencyRows(document.totals, "Total acordado")}${currencyRows(document.paid, "Dinero real imputado")}${currencyRows(document.bonus, "Bonificado")}${currencyRows(document.covered, "Total cubierto")}${currencyRows(document.outstanding, "Pendiente")}</div>
      <div class="summary-card"><div class="eyebrow">DINERO REAL RECIBIDO / REASIGNADO</div>${currencyRows(document.actualReceived, "Aplicado al contrato") || '<div class="muted">Sin pagos registrados.</div>'}<div class="muted">Las bonificaciones no se suman al dinero recibido.</div></div>
    </div>
    ${document.notes ? `<p class="note">${escapeHtml(document.notes)}</p>` : ""}
  `;
}

function monthlyStatementBody(document) {
  const cadence = document.recurringForever ? "Sin fecha final" : `${document.expectedCycles || "—"} ciclos`;
  return `
    <section class="agreement-card">
      <div><span class="eyebrow">MENSUALIDAD ACTUAL</span><strong>${escapeHtml(moneyLabel(document.termAmount, document.termCurrency))} / mes</strong><span>${escapeHtml(document.termTitle || "Mensualidad")}</span></div>
      <div><span class="eyebrow">HISTÓRICO ESTABILIZADO</span><strong>${document.historicalMonthlyAmount > 0 ? `${escapeHtml(moneyLabel(document.historicalMonthlyAmount, document.historicalMonthlyCurrency))} / mes` : "No configurado"}</strong><span>${document.historicalBonusThrough ? `Bonificar faltantes hasta ${escapeHtml(dateLabel(document.historicalBonusThrough))}` : "Sin cierre histórico configurado"}</span></div>
      <div><span class="eyebrow">COBRO / VIGENCIA</span><strong>${document.collectionDay ? `Día ${escapeHtml(document.collectionDay)}` : "Día no definido"}</strong><span>${escapeHtml(cadence)} · desde ${escapeHtml(dateLabel(document.effectiveFrom))}</span>${document.fxPolicy ? `<span>${escapeHtml(document.fxPolicy)}</span>` : ""}</div>
    </section>
    <section class="section">
      <h2>Mensualidades por período</h2>
      <p class="section-copy">La mensualidad no se mezcla con el contrato. Este cuadro compara el abono pactado con los pagos reales de cada mes y hace visible cuántos días antes o después se realizó cada pago.</p>
      ${timingSummaryHtml(document.timingSummary)}
      ${paymentComparisonTable(document.rows || [], { monthly: true })}
    </section>
    <div class="summary-grid">
      <div class="summary-card">
        <div class="eyebrow">PERÍODOS MOSTRADOS</div>
        <div class="summary-row"><span>Períodos pactados</span><strong>${escapeHtml(document.expectedPeriodCount)}</strong></div>
        <div class="summary-row"><span>Períodos cubiertos</span><strong>${escapeHtml(document.paidPeriodCount)}</strong></div>
        <div class="summary-row"><span>Pendientes / a vencer</span><strong>${escapeHtml(document.unpaidPeriodCount)}</strong></div>
        ${currencyRows(document.expectedTotals, "Total histórico / pactado") || '<div class="muted">Sin períodos pactados.</div>'}
        ${currencyRows(document.appliedTotals, "Dinero real aplicado") || ""}
        ${currencyRows(document.bonusTotals, "Bonificado") || ""}
        ${currencyRows(document.coveredTotals, "Total cubierto") || ""}
        ${currencyRows(document.excessTotals, "Excedente liberado") || ""}
      </div>
      <div class="summary-card"><div class="eyebrow">DINERO REAL DEL SERVER</div>${currencyRows(document.actualReceived, "Aplicado al servidor") || '<div class="muted">Sin pagos registrados.</div>'}${currencyRows(document.rawAssociatedReceived, "Movimientos asociados (bruto)") || ""}<div class="muted">La bonificación cubre comercialmente el período pero nunca se registra como dinero recibido.</div></div>
    </div>
    ${document.notes ? `<p class="note">${escapeHtml(document.notes)}</p>` : ""}
  `;
}

function receiptBody(document) {
  const contractEquivalent = document.contractValue && document.contractCurrency
    ? `<div class="summary-row"><span>Valor imputado al acuerdo</span><strong>${escapeHtml(moneyLabel(document.contractValue, document.contractCurrency))}</strong></div>`
    : "";
  const usdEquivalent = document.usdEquivalent != null
    ? `<div class="summary-row"><span>Equivalencia del pago</span><strong>${escapeHtml(moneyLabel(document.usdEquivalent, "USD"))}</strong></div>`
    : "";
  const fx = document.fxRateARSPerUSD
    ? `<div class="summary-row"><span>Dólar aplicado</span><strong>${escapeHtml(moneyLabel(document.fxRateARSPerUSD, "ARS"))} / USD</strong></div>`
    : "";
  const expected = document.expectedAmount != null && document.expectedCurrency
    ? `<div class="summary-row"><span>Importe pactado</span><strong>${escapeHtml(moneyLabel(document.expectedAmount, document.expectedCurrency))}</strong></div>`
    : "";
  const expectedDate = document.expectedDueAt
    ? `<div class="summary-row"><span>Vencimiento pactado</span><strong>${escapeHtml(dateLabel(document.expectedDueAt))}</strong></div>`
    : "";
  const timing = document.timing
    ? `<div class="receipt-timing ${escapeHtml(document.timing.tone)}"><span class="timing-badge ${escapeHtml(document.timing.tone)}">${escapeHtml(document.timing.shortLabel)}</span><strong>${escapeHtml(document.timing.label)}</strong><span>${escapeHtml(shortDateLabel(document.paidAt))} vs. ${escapeHtml(shortDateLabel(document.expectedDueAt))}</span></div>`
    : "";
  const expectedUsd = document.expectedUsdAtPaymentRate != null && document.expectedCurrency !== "USD"
    ? `<div class="summary-row"><span>Pactado expresado en USD al dólar de este pago</span><strong>${escapeHtml(moneyLabel(document.expectedUsdAtPaymentRate, "USD"))}</strong></div>`
    : "";
  const bonification = document.bonifiedAmount != null
    ? `<div class="summary-row"><span>Bonificación aplicada</span><strong>${escapeHtml(moneyLabel(document.bonifiedAmount, document.bonifiedCurrency || document.expectedCurrency || document.currency))}</strong></div>${document.bonificationReason ? `<div class="muted">${escapeHtml(document.bonificationReason)}</div>` : ""}`
    : "";
  const variance = document.variance != null
    ? `<div class="summary-row"><span>Diferencia luego de bonificación</span><strong>${escapeHtml(`${Number(document.variance) > 0 ? "+" : ""}${moneyLabel(document.variance, document.varianceCurrency || document.expectedCurrency || document.currency)}`)}</strong></div>`
    : "";
  return `
    <div class="receipt-amount">${escapeHtml(moneyLabel(document.amount, document.currency))}</div>
    <div class="receipt-label">${escapeHtml(document.periodLabel || "Pago realizado")}</div>
    ${timing}
    <div class="summary receipt-summary">
      <div class="summary-row"><span>Fecha real del pago</span><strong>${escapeHtml(dateLabel(document.paidAt))}</strong></div>
      <div class="summary-row"><span>Concepto</span><strong>${escapeHtml(document.termTitle || document.description || "Pago")}</strong></div>
      ${expected}
      ${expectedDate}
      ${usdEquivalent}
      ${expectedUsd}
      ${contractEquivalent}
      ${fx}
      ${bonification}
      ${variance}
    </div>
    ${document.fxNote ? `<p class="fx-note"><strong>Cotización del período:</strong> ${escapeHtml(document.fxNote)}</p>` : ""}
    ${document.description ? `<p class="note">${escapeHtml(document.description)}</p>` : ""}
  `;
}

function documentHtml(document) {
  const body = document.kind === "contract"
    ? contractBody(document)
    : document.kind === "monthly_statement"
      ? monthlyStatementBody(document)
      : receiptBody(document);
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${escapeHtml(document.title || "Comprobante")}</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#111;background:#fff}
  .page{max-width:1040px;margin:0 auto;padding:38px 42px}
  .top{display:flex;justify-content:space-between;gap:24px;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:18px}
  h1{font-size:27px;line-height:1.1;margin:0 0 8px}h2{font-size:16px;margin:0 0 7px}.badge{display:inline-block;border:1px solid #777;border-radius:999px;padding:5px 9px;font-size:10px;font-weight:700;letter-spacing:.04em}
  .meta{text-align:right;font-size:12px;line-height:1.5}.muted{color:#666;font-size:10px;line-height:1.45;margin-top:3px}.eyebrow{display:block;font-size:9px;font-weight:700;letter-spacing:.09em;color:#666;margin-bottom:6px}
  .parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:18px 0}.party{border:1px solid #ddd;border-radius:10px;padding:12px;font-size:11px;line-height:1.45}.party strong{display:block;font-size:14px;margin-bottom:2px}
  .project{margin:0 0 14px;font-size:11px}.project strong{font-size:14px}.section{margin-top:22px}.section-copy{font-size:10px;color:#555;line-height:1.45;margin:0 0 8px}
  .table-wrap{overflow:visible}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{padding:9px 7px;border-bottom:1px solid #ddd;text-align:left;font-size:10px;vertical-align:top}th{font-size:8.5px;text-transform:uppercase;letter-spacing:.055em;background:#fafafa}.amount{text-align:right;white-space:nowrap}.comparison th:nth-child(1){width:14%}.comparison th:nth-child(2){width:16%}.comparison th:nth-child(3){width:15%}.comparison th:nth-child(4){width:13%}.comparison th:nth-child(5){width:19%}.comparison th:nth-child(6){width:11%}.comparison th:nth-child(7){width:12%}
  .detail-row td{padding-top:3px;background:#fcfcfc}.status-line{margin-top:4px;font-size:9px;font-weight:700}.status.pending{font-weight:700;color:#777}.positive{font-weight:700}.negative{font-weight:700}.empty{padding:12px;border:1px dashed #ccc;border-radius:8px;color:#666;font-size:11px}
  .timing-badge{display:inline-block;border-radius:999px;padding:4px 7px;font-size:8.5px;font-weight:800;white-space:nowrap;border:1px solid #bbb}.timing-badge.early{background:#edf8f0;border-color:#9bc6a5}.timing-badge.ontime{background:#f4f4f4;border-color:#bbb}.timing-badge.late{background:#fff0ef;border-color:#d6a09a}.timing-copy{font-size:8.5px;line-height:1.35;margin-top:5px;color:#555}.timing-overview{display:grid;grid-template-columns:minmax(220px,1.7fr) repeat(3,minmax(90px,.7fr));gap:8px;margin:10px 0 12px}.timing-overview>div{border:1px solid #ddd;border-radius:9px;padding:9px 10px}.timing-overview>div:first-child strong{display:block;font-size:12px}.timing-overview-copy{display:block;font-size:9px;color:#555;margin-top:3px}.timing-stat{text-align:center}.timing-stat strong{display:block;font-size:18px;line-height:1}.timing-stat span{display:block;font-size:8.5px;margin-top:4px;color:#555}.timing-stat.early{background:#f6fbf7}.timing-stat.ontime{background:#fafafa}.timing-stat.late{background:#fff7f6}.receipt-timing{max-width:540px;margin-top:14px;padding:12px;border:1px solid #ccc;border-radius:10px}.receipt-timing.early{background:#f6fbf7;border-color:#a9ceb1}.receipt-timing.ontime{background:#fafafa}.receipt-timing.late{background:#fff7f6;border-color:#dcaaa5}.receipt-timing strong{display:block;font-size:13px;margin-top:7px}.receipt-timing>span:last-child{display:block;font-size:9px;color:#666;margin-top:4px}
  .summary{margin:18px 0 0 auto;max-width:440px;border-top:2px solid #111;padding-top:8px}.summary-row{display:flex;justify-content:space-between;gap:20px;padding:6px 0;border-bottom:1px solid #eee;font-size:10.5px}.summary-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:18px}.summary-card{border:1px solid #ddd;border-radius:10px;padding:12px}.summary-card .summary-row:last-child{border-bottom:0}
  .agreement-card{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;border:1px solid #bbb;border-radius:12px;padding:13px;margin-top:18px}.agreement-card strong{display:block;font-size:15px;margin-bottom:4px}.agreement-card span:not(.eyebrow){font-size:10px;color:#555}
  .note,.fx-note{font-size:10px;line-height:1.5;color:#555;margin-top:18px}.fx-note{padding:9px 11px;border:1px solid #ddd;border-radius:8px;background:#fafafa}.receipt-amount{font-size:38px;font-weight:800;margin-top:26px}.receipt-label{font-size:14px;font-weight:700;margin-top:5px}.receipt-summary{max-width:540px}
  .footer{margin-top:30px;padding-top:12px;border-top:1px solid #ddd;font-size:9px;color:#666;display:flex;justify-content:space-between;gap:16px}
  @media print{.page{max-width:none;padding:14px 16px}.section{break-inside:auto}.summary-card,.agreement-card,.party{break-inside:avoid}thead{display:table-header-group}tr{break-inside:avoid}@page{size:${document.kind === "contract" || document.kind === "monthly_statement" ? "A4 landscape" : "A4 portrait"};margin:8mm}}
  @media(max-width:760px){.page{padding:24px 18px}.top,.parties,.summary-grid,.agreement-card{display:block}.timing-overview{grid-template-columns:1fr 1fr}.timing-overview>div:first-child{grid-column:1/-1}.meta{text-align:left;margin-top:12px}.party,.summary-card,.agreement-card>div{margin-top:9px}.table-wrap{overflow-x:auto}.comparison{min-width:1040px}}
</style>
</head>
<body>
<main class="page">
  <header class="top">
    <div><h1>${escapeHtml(document.title)}</h1><span class="badge">${escapeHtml(document.disclaimer || "Documento comercial")}</span></div>
    <div class="meta"><strong>${escapeHtml(document.reference || "")}</strong><br/>Emitido ${escapeHtml(dateLabel(document.issuedAt))}</div>
  </header>
  <div class="parties">${partyHtml("EMISOR", document.issuer)}${partyHtml("CLIENTE", document.client)}</div>
  <div class="project"><span class="eyebrow">PROYECTO</span><strong>${escapeHtml(document.project || "—")}</strong></div>
  ${body}
  <footer class="footer"><span>Generado desde Owner · WebProfit</span><span>${escapeHtml(document.reference || "")}</span></footer>
</main>
<script>window.addEventListener('load',()=>{setTimeout(()=>window.print(),120)});</script>
</body>
</html>`;
}

export function printOwnerProjectDocument(document) {
  if (!document) return false;
  const popup = window.open("", "_blank");
  if (!popup) return false;
  try { popup.opener = null; } catch {}
  popup.document.open();
  popup.document.write(documentHtml(document));
  popup.document.close();
  return true;
}
